import {Response} from "@/hooks/services/queries/types";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import axiosWithAccounts from "@FRAMEWORK/utils/accountsAxios";
import {UseQueryOptions, useQuery} from "@tanstack/react-query";

type CustomerMe = {
	webCustNo: string; // 웹고객번호
	custId: string; // 고객아이디
	custNmKor: string; // 고객이름(한글)
	birthDt: string; // 생년월일
	genderCd: string; // 성별
	mobileTel: string; // 휴대폰번호
	fdCd: string; // 내/외국인구분코드
	custGradeCd: string; // 회원등급코드
	custGradeNm: string; // 회원등급명
	nonUser: boolean;
	rervNo?: string;
	nonCustName?: string;
};

const EMPTY_CUSTOMER: CustomerMe = {
	webCustNo: "",
	custId: "",
	custNmKor: "",
	birthDt: "",
	genderCd: "",
	mobileTel: "",
	fdCd: "",
	custGradeCd: "",
	custGradeNm: "",
	nonUser: true,
};

const key = "useCustomerMe";
const getPath = (): string | null => `${publicRuntimeConfig.ENV_PRODUCT_URL}/acct/api/account/customerMe`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

/** 400 HTML 등 비정상 응답이 data로 들어와도 body 접근이 깨지지 않게 정규화 */
const normalizeCustomerMe = (payload: unknown, status: number): Response<CustomerMe> => {
	if (!isRecord(payload)) {
		return {
			code: String(status),
			message: "invalid customerMe response",
			body: EMPTY_CUSTOMER,
		};
	}

	const body = payload.body;
	if (!isRecord(body)) {
		return {
			code: typeof payload.code === "string" ? payload.code : String(status),
			message: typeof payload.message === "string" ? payload.message : "empty customerMe body",
			body: EMPTY_CUSTOMER,
		};
	}

	return payload as Response<CustomerMe>;
};

const fetchCustomerMe = async (): Promise<Response<CustomerMe>> => {
	const result = await axiosWithAccounts({
		url: getPath(),
		method: "POST",
	});
	// axiosWithAccounts는 4xx를 throw하지 않음. HTML/비정상 payload는 비로그인으로 취급
	if (result.status >= 400) {
		return normalizeCustomerMe(null, result.status);
	}
	return normalizeCustomerMe(result.data, result.status);
};

const customerMeAsync = async () => fetchCustomerMe();

/** ```
 * API를 통한 로그인 상태 정보 조회
 *   `accounts.ybtour.co.kr/proxy/oauth2/token`
 * parameter
 * ```*/
const useCustomerMe = (options?: Partial<UseQueryOptions<Response<CustomerMe>, Error>>) =>
	useQuery({
		queryKey: [key, getPath()],
		queryFn: fetchCustomerMe,
		staleTime: 1000 * 60,
		gcTime: 1000 * 60,
		refetchOnWindowFocus: false,
		throwOnError: false,
		retry: 3,
		...options,
	});

export default useCustomerMe;
export {getPath, customerMeAsync};
export type {CustomerMe};
